import { coercePrimitiveValue, createSchemaPropertyList, ensureQuery, ensureQueryByShape, findMapping, findShapeFormConfig, followRef, getArgsSchema, getBuiltInPropertyNames, getConflictingProperties, getRef, getShapeById, interfaceToUnion, isAllOfSchema, isCachedShape, isEnumSchema, isExtendsSchema, isInterfaceShape, isObjectSchema, isScalar, isUnionSchema, parseReturnShape, refItemToNamespacedShapeName, refItemToShape } from '@takeshape/schema';
import { arrayStartsWith, formatShapeName, isDefined, pascalCase, value } from '@takeshape/util';
import difference from 'lodash/difference.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import orderBy from 'lodash/orderBy.js';
import stringify from 'stringify-object';
import { addInputSuffix, shouldUseInputSuffix } from './naming.js';
import { detectPaginationScheme, jsonPathToArray } from "./pagination/index.js";
const SITES_SHAPE_ID = 'SITE';
const DEFAULT_MAX_ABSOLUTE_QUERY_DEPTH = 10;
const DEFAULT_MAX_QUERY_DEPTH = 2;
const orderByNames = (properties) => orderBy(properties, [([propName]) => propName], ['asc']);
export function formatIndexedProps(properties) {
    return orderByNames(properties)
        .filter(([, prop]) => {
        return (prop.type === 'string' || prop.type === 'boolean') && !prop['@sensitive'] && !prop['@deprecationReason'];
    })
        .map(([name]) => name)
        .join(' ');
}
// Return arguments that are needed to query this property.
export function getPropArgs(projectSchema, path, prop, paginationScheme) {
    const args = prop['@args'];
    if (args) {
        const argsValues = {};
        // TODO support more pagination schemes
        if (paginationScheme?.type === 'cursor') {
            argsValues[paginationScheme.pageSizeArg] = 5;
        }
        const argsSchema = getArgsSchema(projectSchema, args);
        if (argsSchema) {
            if (argsSchema.required && difference(argsSchema.required, Object.keys(argsValues)).length) {
                return { missingRequiredArgs: true, args: '' };
            }
            const [argsStr] = printArgs(projectSchema, path, argsSchema, {
                args: argsValues
            });
            return { missingRequiredArgs: false, args: parens(argsStr) };
        }
    }
    return { missingRequiredArgs: false, args: '' };
}
function join(names) {
    return names.filter((str) => str !== '').join(' ');
}
const builtInPropertyNames = getBuiltInPropertyNames();
function addTypename(body) {
    return body !== '' ? `__typename ${body}` : body;
}
function isId(propSchema) {
    return propSchema['@tag'] === 'id';
}
function printObjectFragment(context, prop) {
    const { onlyFormProps, shapeName, form, path, projectSchema } = context;
    return join(orderByNames(createSchemaPropertyList(projectSchema, prop).getNodes()).map(([propName, propSchema]) => {
        if (onlyFormProps &&
            shapeName &&
            form?.order &&
            !form.order.includes(propName) &&
            !builtInPropertyNames.has(propName) &&
            !isId(propSchema)) {
            return '';
        }
        return printQueryBody({
            ...context,
            form: form?.properties?.[propName],
            propName,
            path: [...path, propName],
            isLocalProp: isLocalFragment(propSchema)
        }, propSchema);
    }));
}
function getIsConflictingPath(currentPathLength, conflicts) {
    if (conflicts.size === 0) {
        return value(false);
    }
    return (path) => path.length === currentPathLength + 1 && conflicts.has(path[currentPathLength]);
}
function printUnionFragment(context, prop) {
    const { projectSchema } = context;
    if (!prop.oneOf.length) {
        return '';
    }
    const inlineFragment = prop.oneOf.length > 1;
    const shapes = prop.oneOf
        .map((childSchema) => {
        const ref = getRef(projectSchema, childSchema);
        return ref && refItemToShape(projectSchema, ref);
    })
        .filter(isDefined);
    const isConflictingPath = getIsConflictingPath(context.path.length, getConflictingProperties(projectSchema, shapes));
    const unionBody = join(prop.oneOf.map((childSchema) => {
        const ref = getRef(projectSchema, childSchema);
        if (!ref) {
            return '';
        }
        const queryBody = printQueryBody({ ...context, propName: undefined, isConflictingPath }, childSchema);
        if (queryBody === '') {
            return '';
        }
        return inlineFragment ? `... on ${refItemToNamespacedShapeName(projectSchema, ref)} {${queryBody}}` : queryBody;
    }));
    return addTypename(unionBody);
}
function isTakeShapeMapping(mapping) {
    return mapping.startsWith('shapedb');
}
function isLocalFragment(propertySchema) {
    return !propertySchema['@resolver'] && Boolean(findMapping(propertySchema, isTakeShapeMapping));
}
function getIsPaginationPath(startIndex, paginationScheme) {
    const itemsPath = jsonPathToArray(paginationScheme.itemsPath);
    let paginationPaths;
    if (paginationScheme.type === 'cursor') {
        paginationPaths = [
            jsonPathToArray(paginationScheme.hasMorePath),
            jsonPathToArray(paginationScheme.cursorPath),
            itemsPath
        ];
    }
    else if (paginationScheme.type === 'page') {
        paginationPaths = [jsonPathToArray(paginationScheme.pageTotalPath), itemsPath];
    }
    else if (paginationScheme.type === 'offset') {
        paginationPaths = [jsonPathToArray(paginationScheme.itemTotalPath), itemsPath];
    }
    return (path) => {
        const rest = path.slice(startIndex);
        const paginationPath = paginationPaths.find((paginationPath) => arrayStartsWith(paginationPath, rest));
        if (paginationPath) {
            const distance = paginationPath.length - rest.length;
            return { distance, isMatch: true, shouldExclude: false };
        }
        // exclude extra properties not in the itemsPath
        return {
            isMatch: false,
            distance: -1,
            shouldExclude: !arrayStartsWith(rest, itemsPath)
        };
    };
}
function trimBrackets(selectionSet) {
    return selectionSet.replace(/^\s*{\s*/, '').replace(/\s*}\s*$/, '');
}
export function printQueryBody(context, rawProp) {
    const { predicate, includeDeprecated = false, ignoreGenerated, projectSchema, isConflictingPath, propName, shapeName } = context;
    if (rawProp['@deprecationReason'] && !includeDeprecated) {
        return '';
    }
    if (context.isPaginationPath?.(context.path)?.shouldExclude) {
        // Bail out if we are an unnecessary field in a pagination fragment
        return '';
    }
    if (rawProp['@tag'] === 'generated' && ignoreGenerated) {
        return '';
    }
    if (predicate && !predicate(context, rawProp)) {
        return '';
    }
    const fieldName = propName && isConflictingPath?.(context.path) && shapeName ? `${shapeName}_${propName}: ${propName}` : propName;
    if (rawProp['@graphql']?.json) {
        return fieldName ?? '';
    }
    const newContext = { ...context };
    let maybeRefSchema = rawProp.items ?? rawProp;
    const ref = getRef(projectSchema, maybeRefSchema);
    let paginationScheme;
    if (ref) {
        const shape = refItemToShape(projectSchema, ref);
        if (!shape) {
            return '';
        }
        // Do not check or increment query depth when a ref refers to a union or enum
        if (!isEnumSchema(shape.schema) && !isUnionSchema(shape.schema) && !isInterfaceShape(shape)) {
            newContext.absoluteQueryDepth += 1;
            const paginationPathMatch = newContext.isPaginationPath?.(newContext.path);
            if ((!paginationPathMatch || !paginationPathMatch.isMatch) && !newContext.isLocalProp) {
                newContext.queryDepth += 1;
            }
            if (!newContext.isPaginationPath || paginationPathMatch?.distance === 0) {
                paginationScheme = detectPaginationScheme(projectSchema, rawProp);
                if (paginationScheme) {
                    newContext.isPaginationPath = getIsPaginationPath(newContext.path.length, paginationScheme);
                }
                else {
                    newContext.isPaginationPath = undefined;
                }
            }
            if (newContext.absoluteQueryDepth > newContext.maxAbsoluteQueryDepth ||
                newContext.queryDepth > newContext.maxQueryDepth) {
                return '';
            }
        }
        newContext.shapeName = shape.name;
        newContext.propName = undefined;
        if (isInterfaceShape(shape)) {
            maybeRefSchema = interfaceToUnion(projectSchema, ref);
        }
        const form = findShapeFormConfig(projectSchema, newContext.shapeName, 'default');
        if (form) {
            newContext.form = form;
        }
    }
    const printBody = (body) => {
        const { projectSchema } = newContext;
        if (body === '') {
            return '';
        }
        if (fieldName) {
            const { missingRequiredArgs, args } = getPropArgs(projectSchema, context.path, rawProp, paginationScheme);
            // Omit the property if we cannot provide the required args
            if (missingRequiredArgs) {
                return '';
            }
            return `${fieldName}${args} {${body}}`;
        }
        return body;
    };
    if (newContext.shapeName && newContext.overrides?.[newContext.shapeName]) {
        return printBody(trimBrackets(newContext.overrides[newContext.shapeName]));
    }
    const prop = followRef(projectSchema, maybeRefSchema);
    if (isObjectSchema(prop) || isAllOfSchema(prop) || isExtendsSchema(prop)) {
        return printBody(printObjectFragment(newContext, prop));
    }
    if (isUnionSchema(prop)) {
        return printBody(printUnionFragment(newContext, prop));
    }
    // Inline unions cannot be processed correctly
    if (!fieldName) {
        return '';
    }
    const { missingRequiredArgs, args } = getPropArgs(projectSchema, context.path, rawProp, paginationScheme);
    return missingRequiredArgs ? '' : `${fieldName}${args}`;
}
export function getQueryBody(shapeId, projectSchema, options) {
    const shape = getShapeById(projectSchema, shapeId);
    if (!shape) {
        throw new Error(`getQueryBody failed no shape with id "${shapeId}"`);
    }
    const { predicate, ...printQueryBodyOptions } = options ?? {};
    const params = {
        predicate,
        projectSchema,
        absoluteQueryDepth: 0,
        queryDepth: 1,
        maxAbsoluteQueryDepth: DEFAULT_MAX_ABSOLUTE_QUERY_DEPTH,
        maxQueryDepth: DEFAULT_MAX_QUERY_DEPTH,
        shapeName: shape.name,
        onlyFormProps: shapeId !== SITES_SHAPE_ID,
        includeDeprecated: false,
        path: [],
        form: findShapeFormConfig(projectSchema, shape.name, 'default'),
        ...printQueryBodyOptions
    };
    return printQueryBody(params, shape.schema);
}
export const shapePathToTypeName = (shapePath) => {
    const [shapeName, ...path] = shapePath;
    const prefix = shapeName
        .split('_')
        .map((name) => formatShapeName(name))
        .join('_');
    return path.length === 0 ? prefix : [prefix, pascalCase(path), 'Property'].join('');
};
export const schemaPathToTypeName = (schemaPath) => {
    return shapePathToTypeName(schemaPath.slice(1).map(String));
};
export function resolveStringToOmittedValue(path) {
    return (obj) => (get(obj, path) === undefined ? '' : '[OMITTED]');
}
export function getEnumTypeName(shapeName, propertyName) {
    return pascalCase([shapeName, propertyName]);
}
export function getDefaultResponseName(operationName) {
    return `${pascalCase(operationName)}Response`;
}
function getPropType(args) {
    const { projectSchema, path, prop, propName, isRequired } = args;
    let type;
    // TODO add @tag to _id property in schema defaults and use this hack until then
    if (propName === '_id') {
        type = 'ID';
    }
    else if (prop.type === 'array' && prop.items) {
        type = `[${getPropType({ projectSchema, path, prop: prop.items, propName, isRequired })}]`;
    }
    else if (prop['@tag'] !== undefined) {
        const tag = prop['@tag'];
        if (tag === 'id') {
            type = 'ID';
        }
        else if (prop['@tag'] === 'draftjs' || prop['@tag'] === 'json') {
            type = 'JSON';
        }
        else if (prop.type === undefined) {
            type = pascalCase(prop['@tag']);
        }
    }
    else if (prop.type === 'object' && isEmpty(prop.properties)) {
        type = 'JSONObject';
    }
    else if (prop.type === 'object') {
        type = `${shapePathToTypeName(path)}${pascalCase(propName)}`;
    }
    else if (prop.type === 'string') {
        type = 'String';
    }
    else if (prop.type === 'number') {
        type = 'Float';
    }
    else if (prop.type === 'integer') {
        type = 'Int';
    }
    else if (prop.type === 'boolean') {
        type = 'Boolean';
    }
    else if (isEmpty(prop)) {
        type = 'JSON';
    }
    else {
        const ref = getRef(projectSchema, prop);
        const shape = ref && refItemToShape(projectSchema, ref);
        if (shape) {
            if (isEmpty(shape.schema)) {
                type = 'JSON';
            }
            else {
                const namespacedShapeName = refItemToNamespacedShapeName(projectSchema, ref);
                type = shouldUseInputSuffix(shape) ? addInputSuffix(namespacedShapeName) : namespacedShapeName;
            }
        }
    }
    if (!type) {
        throw new Error(`Unknown type mapping for arg "${propName}"`);
    }
    return isRequired ? `${type}!` : type;
}
function getArgType(projectSchema, path, argSchema, propName) {
    const isRequired = Boolean(argSchema.required?.includes(propName));
    const prop = argSchema.properties[propName];
    return getPropType({ projectSchema, path, prop, propName, isRequired });
}
function parens(value) {
    return value ? `(${value})` : '';
}
function curly(value) {
    return value ? `{${value}}` : '';
}
function serializeArgValue(argsSchema, argName, argValue) {
    const argSchema = argsSchema.properties[argName];
    if (!argSchema) {
        throw new Error(`Unknown arg "${argName}"`);
    }
    return stringify(coercePrimitiveValue(argSchema, argValue), { singleQuotes: false });
}
export function printArgs(projectSchema, schemaPath, argsSchema, options) {
    const validArgNames = Object.keys(argsSchema.properties).sort();
    const required = new Set(argsSchema.required);
    let vars;
    if (options?.allVars) {
        vars = validArgNames;
    }
    else if (options?.varsForMissing) {
        vars = validArgNames.filter((argName) => options?.args?.[argName] === undefined && required.has(argName));
    }
    else {
        vars = [];
    }
    const varSet = new Set(vars);
    const args = options?.args ?? {};
    const argsStr = validArgNames
        .filter((name) => args[name] !== undefined || varSet.has(name))
        .map((name) => `${name}: ${varSet.has(name) ? `$${name}` : serializeArgValue(argsSchema, name, args[name])}`)
        .join(' ');
    const varsStr = vars.length
        ? vars.map((varName) => `$${varName}: ${getArgType(projectSchema, schemaPath, argsSchema, varName)}`).join(' ')
        : '';
    return [argsStr, varsStr];
}
export function getGraphQLQueryString(projectSchema, queryRef, options) {
    const { name, query, ref } = ensureQuery(projectSchema, queryRef);
    const type = ref.shapeName.toLowerCase();
    const { shapeName, template } = parseReturnShape(projectSchema, query.shape);
    if (template) {
        throw new Error('Unexpected template type please run flattenTemplates');
    }
    const { args, varsForMissing, ...printQueryBodyOptions } = options ?? {};
    let queryBody;
    if (!isScalar(shapeName) && shapeName !== 'object') {
        const shape = projectSchema.shapes[shapeName];
        const paginationScheme = detectPaginationScheme(projectSchema, query);
        queryBody = printQueryBody({
            projectSchema,
            absoluteQueryDepth: 0,
            queryDepth: 1,
            includeDeprecated: false,
            onlyFormProps: shape.id !== SITES_SHAPE_ID,
            ...printQueryBodyOptions,
            maxAbsoluteQueryDepth: printQueryBodyOptions.maxAbsoluteQueryDepth ?? DEFAULT_MAX_ABSOLUTE_QUERY_DEPTH,
            maxQueryDepth: printQueryBodyOptions.maxQueryDepth ?? DEFAULT_MAX_QUERY_DEPTH,
            shapeName: shape.name,
            path: [queryRef],
            isPaginationPath: paginationScheme && getIsPaginationPath(1, paginationScheme),
            form: findShapeFormConfig(projectSchema, shape.name, 'default')
        }, shape.schema);
    }
    if (options?.bodyOnly) {
        return curly(queryBody);
    }
    let argsStr = '';
    let varsStr = '';
    if (options?.args ?? options?.varsForMissing ?? options?.allVars) {
        const argsSchema = query.args ? getArgsSchema(projectSchema, query.args) : undefined;
        if (argsSchema) {
            [argsStr, varsStr] = printArgs(projectSchema, [name, 'properties'], argsSchema, options);
        }
    }
    return `${type} ${parens(varsStr)}{ ${name}${parens(argsStr)}${curly(queryBody)} }`;
}
export function getFieldsPredicate(fields) {
    const fieldSet = new Set(fields);
    return ({ queryDepth, propName }) => (queryDepth === 1 && propName ? fieldSet.has(propName) : true);
}
function formatVarDefinition(shapeName) {
    return `$terms: String, $sort: [TSSearchSortInput], $from: Int, $size: Int, $filter: JSONObject, $where: TSWhere${pascalCase(shapeName)}Input, $onlyEnabled: Boolean`;
}
function formatVarAssignment() {
    return 'terms: $terms, sort: $sort, from: $from, size: $size, filter: $filter, where: $where, onlyEnabled: $onlyEnabled';
}
export function generateShapeProps(projectSchema, shape, fields, options) {
    let props;
    const schemaPropertyList = createSchemaPropertyList(projectSchema, shape);
    const isList = fields?.length;
    if (isList) {
        const fieldPredicate = getFieldsPredicate(fields);
        props = getQueryBody(shape.id, projectSchema, {
            maxAbsoluteQueryDepth: 1,
            maxQueryDepth: 2,
            predicate: fieldPredicate,
            ...options
        });
    }
    else {
        props = formatIndexedProps(schemaPropertyList.getNodes());
    }
    return props;
}
export function generateShapeSearchQuery(projectSchema, shape, fields, options) {
    const props = generateShapeProps(projectSchema, shape, fields, options);
    let query;
    if (isCachedShape(shape)) {
        query = `
query search($terms: String, $sort: [TSSearchSortInput], $from: Int, $size: Int, $filter: JSONObject, $where: TSWhereInput){
  search(shapeNames:"${shape.name}" terms: $terms sort: $sort from: $from size: $size filter: $filter where: $where) {
    total
    results {
      ... on ${shape.name} {
        ${props}
      }
    }
  }
}
`;
    }
    else {
        const operation = ensureQueryByShape(projectSchema, shape.name, 'shapedb:list');
        query = `
query ${operation}(${formatVarDefinition(shape.name)}) {
  search: ${operation}(${formatVarAssignment()}) {
    total
    results: items {
      ${props}
    }
  }
}
`;
    }
    return query;
}
